// src/runtime/KnowledgeEvolutionRuntime/writer.ts
// ============================================
// Knowledge Writer - 知识写回器
//
// 职责：
//   1. 候选知识写入 30_Inbox/{date}/（全自动，无需人工）
//   2. 低风险自动通过的候选知识写入正式知识库 09_Knowledge（知识中心）/Cards/{category}/
//   3. 生成"今天公司学到了什么"日报 → 10_Operations（运营层）/Daily/今日所学-{date}.md
//
// 实现：
//   - 浏览器模式：通过 BrowserVaultReader.writeFile()（/vault-api/write）
//   - Node 模式：直接 fs 写入
//   - 全部路径基于 vaultConfig.vaultRoot（唯一真相源）
// ============================================

import * as fs from 'fs';
import * as path from 'path';
import { vaultConfig } from '../shared/vaultConfig';
import { BrowserVaultReader } from '../shared/BrowserVaultReader';
import config from './config';
import {
  KNOWLEDGE_SOURCE_LABEL,
  RISK_LEVEL_LABEL,
  CANDIDATE_STATUS_LABEL,
  type CandidateKnowledge,
  type DailyKnowledgeReport,
} from './types';

/** 写入结果 */
export interface VaultWriteResult {
  success: boolean;
  filePath?: string;
  error?: string;
}

export class KnowledgeWriter {
  private reader = BrowserVaultReader.getInstance();

  // ============================================
  // 1. 候选知识 → 30_Inbox/
  // ============================================

  /** 写入候选知识到 30_Inbox/{date}/{id}.md（全自动） */
  async writeCandidateToInbox(
    candidate: CandidateKnowledge,
    date: string
  ): Promise<VaultWriteResult> {
    const relPath = `${config.inboxDir}/${date}/${candidate.id}.md`;
    const content = this.serializeCandidate(candidate, 'inbox');
    return this.writeVaultFile(relPath, content);
  }

  /** 批量写入候选知识到 Inbox */
  async writeCandidatesToInbox(
    candidates: CandidateKnowledge[],
    date: string
  ): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;
    for (const c of candidates) {
      const r = await this.writeCandidateToInbox(c, date);
      if (r.success) success++;
      else failed++;
    }
    return { success, failed };
  }

  // ============================================
  // 2. 正式知识库（低风险自动通过 / CEO 确认）
  // ============================================

  /** 写入正式知识库 09_Knowledge（知识中心）/Cards/{category}/{date}-{id}.md */
  async writeToFormalKnowledgeBase(
    candidate: CandidateKnowledge,
    date: string
  ): Promise<VaultWriteResult> {
    const safeCategory = this.sanitize(candidate.targetCategory || 'General');
    const relPath =
      `${config.knowledgeBaseDir}/${safeCategory}/${date}-${candidate.id}.md`;
    const content = this.serializeCandidate(candidate, 'formal');
    return this.writeVaultFile(relPath, content);
  }

  // ============================================
  // 3. "今天公司学到了什么"日报
  // ============================================

  /** 生成并写入日报 → 10_Operations（运营层）/Daily/今日所学-{date}.md */
  async writeDailyReport(report: DailyKnowledgeReport): Promise<VaultWriteResult> {
    const relPath = `${config.reportDir}/今日所学-${report.date}.md`;
    const content = this.serializeReport(report);
    return this.writeVaultFile(relPath, content);
  }

  // ============================================
  // 4. 读取 Inbox 候选知识（供 UI 展示 + 状态更新）
  // ============================================

  /** 列出 30_Inbox 下所有候选知识文件 */
  async listInboxCandidates(): Promise<Array<{ date: string; id: string; title: string }>> {
    try {
      const files = await this.reader.listFilesRecursive(config.inboxDir);
      return files.map((f) => {
        const segs = f.path.replace(/\\/g, '/').split('/');
        const fileName = segs[segs.length - 1]?.replace(/\.md$/i, '') || f.name;
        const date = segs[segs.length - 2] || '';
        return { date, id: fileName, title: fileName };
      });
    } catch {
      return [];
    }
  }

  // ============================================
  // 序列化
  // ============================================

  /** 候选知识 → Markdown（frontmatter + 正文） */
  serializeCandidate(candidate: CandidateKnowledge, kind: 'inbox' | 'formal'): string {
    return [
      '---',
      `type: ${kind === 'inbox' ? 'candidate_knowledge' : 'knowledge_card'}`,
      `title: ${candidate.title}`,
      `summary: ${candidate.summary}`,
      `source: ${candidate.source}`,
      `source_ref: ${candidate.sourceRef}`,
      `risk_level: ${candidate.riskLevel}`,
      `status: ${candidate.status}`,
      `category: ${candidate.targetCategory}`,
      `score: ${candidate.score}`,
      `tags: [${candidate.tags.join(', ')}]`,
      `detected_at: ${candidate.detectedAt}`,
      `created_at: ${new Date().toISOString()}`,
      '---',
      '',
      `# ${candidate.title}`,
      '',
      candidate.content,
      '',
      '---',
      '',
      '## 元信息',
      '',
      `- 来源：${KNOWLEDGE_SOURCE_LABEL[candidate.source] || candidate.source}（${candidate.sourceRef}）`,
      `- 风险等级：${RISK_LEVEL_LABEL[candidate.riskLevel] || candidate.riskLevel}`,
      `- 状态：${CANDIDATE_STATUS_LABEL[candidate.status] || candidate.status}`,
      `- 价值评分：${candidate.score}/100`,
      candidate.feedback ? `- CEO 备注：${candidate.feedback}` : '',
      '',
    ]
      .filter((l) => l !== '')
      .join('\n');
  }

  /** 日报 → Markdown */
  serializeReport(report: DailyKnowledgeReport): string {
    const lines = [
      '---',
      'type: daily_knowledge_report',
      `date: ${report.date}`,
      `new_candidates: ${report.newCandidates}`,
      `auto_approved: ${report.autoApproved}`,
      `needs_review: ${report.needsReview}`,
      `needs_ceo: ${report.needsCeo}`,
      `created_at: ${report.generatedAt}`,
      '---',
      '',
      `# 今天公司学到了什么（${report.date}）`,
      '',
      '> 由 Knowledge Evolution Runtime 每天 23:00 自动生成',
      '',
      '## 今日概览',
      '',
      '| 指标 | 数值 |',
      '| --- | --- |',
      `| 新增候选知识 | ${report.newCandidates} |`,
      `| 低风险自动入库 | ${report.autoApproved} |`,
      `| 待 CEO 确认（中风险） | ${report.needsReview} |`,
      `| 必须 CEO 确认（高风险） | ${report.needsCeo} |`,
      '',
    ];
    if (report.insights.length > 0) {
      lines.push('## 今日洞察', '');
      report.insights.slice(0, 10).forEach((ins, i) => {
        lines.push(
          `${i + 1}. **${ins.title}**（来源：${KNOWLEDGE_SOURCE_LABEL[ins.source] || ins.source}）`,
          `   ${ins.summary}`,
          ''
        );
      });
    } else {
      lines.push('## 今日洞察', '', '今日未提取到新的候选知识。', '');
    }
    lines.push(
      '---',
      '',
      '> 候选知识暂存区：`30_Inbox/{date}/`，可在知识中心「知识演化」面板执行确认入库 / 拒绝归档。',
      ''
    );
    return lines.join('\n');
  }

  // ============================================
  // 内部工具
  // ============================================

  /** 统一写入入口：浏览器走 /vault-api/write，Node 走 fs */
  private async writeVaultFile(
    relPath: string,
    content: string
  ): Promise<VaultWriteResult> {
    if (vaultConfig.isBrowser) {
      const ok = await this.reader.writeFile(relPath, content);
      if (ok) return { success: true, filePath: relPath };
      return { success: false, error: 'BrowserVaultReader.writeFile 失败' };
    }
    try {
      const absPath = path.join(vaultConfig.vaultRoot, relPath);
      fs.mkdirSync(path.dirname(absPath), { recursive: true });
      fs.writeFileSync(absPath, content, 'utf-8');
      return { success: true, filePath: relPath };
    } catch (err: any) {
      return { success: false, error: err?.message || String(err) };
    }
  }

  /** 文件名安全化 */
  private sanitize(name: string): string {
    return name.replace(/[\\/:*?"<>|\s]+/g, '-').replace(/^-+|-+$/g, '') || 'General';
  }
}

export default KnowledgeWriter;
